import { useEffect, useRef, useState } from 'react';
import {
  ChatBubbleLeftRightIcon,
  ClipboardDocumentIcon,
  DocumentPlusIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  QueueListIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

import type { LexSelectionContext } from '../../types/runtime';
import {
  MATTER_INTELLIGENCE_SELECTION_EVENT,
  type MatterIntelligenceSelectionEventDetail,
} from '@/shared/matterIntelligenceSelection';

type SelectionAction =
  | 'summarize'
  | 'ask'
  | 'add_to_prompt'
  | 'note'
  | 'pin'
  | 'draft'
  | 'compare'
  | 'dates'
  | 'fact_note'
  | 'chronology_entry'
  | 'deposition_question'
  | 'issue_statement'
  | 'draft_outline'
  | 'court_rule_check'
  | 'clause_benchmark'
  | 'matter_intelligence';

interface Props {
  onAction: (action: SelectionAction, selection: LexSelectionContext) => void;
  onCompareCancel?: () => void;
  matterIntelligenceOnly?: boolean;
}

interface SelectionRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface CompareBaseSelection {
  selection: LexSelectionContext;
  rects: SelectionRect[];
}

function newSelectionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `selection-${crypto.randomUUID()}`;
  }
  return `selection-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

function numberFromDataset(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function metadataFromDataset(value: string | undefined): Record<string, unknown> {
  if (!value) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : {};
  } catch {
    return {};
  }
}

function elementFromNode(node: Node | null): Element | null {
  if (!node) return null;
  return node.nodeType === Node.ELEMENT_NODE
    ? (node as Element)
    : node.parentElement;
}

function sourceFromRange(range: Range): HTMLElement | null {
  const start = elementFromNode(range.startContainer)?.closest<HTMLElement>('[data-lex-selectable="true"]');
  if (start) return start;
  return elementFromNode(range.commonAncestorContainer)?.closest<HTMLElement>('[data-lex-selectable="true"]') ?? null;
}

function selectionRectsFromRange(range: Range): SelectionRect[] {
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  return Array.from(range.getClientRects())
    .filter((rect) => rect.width > 2 && rect.height > 4)
    .map((rect) => ({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    }))
    .filter((rect) => (
      rect.left < viewportWidth &&
      rect.left + rect.width > 0 &&
      rect.top < viewportHeight &&
      rect.top + rect.height > 0
    ))
    .slice(0, 80);
}

function positionFromRects(rects: SelectionRect[]): { top: number; left: number } | null {
  if (rects.length === 0) return null;
  const first = rects[0];
  const minTop = Math.min(...rects.map((rect) => rect.top));
  const minLeft = Math.min(...rects.map((rect) => rect.left));
  const maxRight = Math.max(...rects.map((rect) => rect.left + rect.width));
  const centerLeft = minLeft + Math.max(0, maxRight - minLeft) / 2;
  return {
    top: Math.max(8, minTop - 54),
    left: Math.min(window.innerWidth - 420, Math.max(8, centerLeft - 120)),
  };
}

function selectionFromRange(selection: Selection, range: Range, rects: SelectionRect[]): LexSelectionContext | null {
  const rawText = selection.toString().replace(/\s+/g, ' ').trim();
  if (rawText.length < 3) return null;

  const source = sourceFromRange(range);
  if (!source) return null;
  const dataset = source.dataset;
  const metadata = metadataFromDataset(dataset.lexSelectionMetadata);

  return {
    id: newSelectionId(),
    text: rawText.slice(0, 12000),
    sourceSurface: dataset.lexSelectionSource || 'browser_selection',
    label: dataset.lexSelectionLabel || null,
    documentId: dataset.lexDocumentId || null,
    versionId: dataset.lexDocumentVersionId || null,
    documentTitle: dataset.lexDocumentTitle || null,
    matterId: dataset.lexMatterId || null,
    clientId: dataset.lexClientId || null,
    page: numberFromDataset(dataset.lexPage),
    evidenceId: dataset.lexEvidenceId || null,
    citationId: dataset.lexCitationId || null,
    messageId: dataset.lexMessageId || null,
    answerKind: dataset.lexAnswerKind || null,
    createdAt: new Date().toISOString(),
    metadata: {
      ...metadata,
      selection_rect_count: rects.length,
      selection_anchor: rects[0] ?? null,
    },
  };
}

/**
 * Selection actions as a grouped catalog (David, 2026-08-05: the highlight
 * menu must read as categorized work, not a flat wall of verbs).
 *
 * Every action carries a glyph and a plain description, so a lawyer never has
 * to guess what "Chrono" or "Depo Q" produces. Rendering derives from this
 * list, so a new action is added here once and appears grouped, glyphed, and
 * explained — the same derive-don't-duplicate rule the capability catalog uses.
 */
interface SelectionActionDefinition {
  action: SelectionAction;
  emoji: string;
  label: string;
  /** Tooltip: what the lawyer gets. */
  hint: string;
  /** Visually primary within its group. */
  primary?: boolean;
}

interface SelectionActionGroup {
  key: string;
  heading: string;
  actions: SelectionActionDefinition[];
}

const SELECTION_ACTION_GROUPS: SelectionActionGroup[] = [
  {
    key: 'ask',
    heading: '⚡ Ask & use',
    actions: [
      { action: 'summarize', emoji: '✨', label: 'Summarize', hint: 'Summarize the selected passage.' },
      { action: 'ask', emoji: '💬', label: 'Ask Lex', hint: 'Ask a question about this passage.' },
      { action: 'add_to_prompt', emoji: '📋', label: 'Add to prompt', hint: 'Add the passage to the composer without sending.' },
      { action: 'compare', emoji: '🔀', label: 'Compare', hint: 'Compare this passage against a second selection.' },
    ],
  },
  {
    key: 'save',
    heading: '📌 Save to the matter',
    actions: [
      { action: 'matter_intelligence', emoji: '🧠', label: 'Matter Intelligence', hint: 'Save as lawyer-approved matter context Lex can rely on later.', primary: true },
      { action: 'note', emoji: '🗒️', label: 'Note', hint: 'Save the passage as a note.' },
      { action: 'pin', emoji: '📎', label: 'Pin', hint: 'Pin the passage to this conversation.' },
    ],
  },
  {
    key: 'work_product',
    heading: '📝 Turn into work product',
    actions: [
      { action: 'fact_note', emoji: '🧾', label: 'Fact note', hint: 'Fact, source, significance, confidence, follow-up.' },
      { action: 'chronology_entry', emoji: '🕒', label: 'Chronology', hint: 'Date, event, parties, source, open issue.' },
      { action: 'deposition_question', emoji: '❓', label: 'Depo questions', hint: 'Objective, questions, exhibit hook, follow-ups, risk.' },
      { action: 'issue_statement', emoji: '⚠️', label: 'Issue', hint: 'Issue, support, why it matters, counterpoint, next step.' },
      { action: 'draft_outline', emoji: '🗂️', label: 'Outline', hint: 'Point heading, facts, evidence, draft language, gaps.' },
      { action: 'draft', emoji: '✍️', label: 'Draft', hint: 'Draft from the selected passage.' },
    ],
  },
  {
    key: 'check',
    heading: '⚖️ Check against authority',
    actions: [
      { action: 'court_rule_check', emoji: '⚖️', label: 'Rule check', hint: 'Flag court-rule, deadline, service, or filing issues — verified, never guessed.' },
      { action: 'dates', emoji: '📅', label: 'Dates', hint: 'Extract the dates in the passage.' },
      { action: 'clause_benchmark', emoji: '📑', label: 'Benchmark clause', hint: 'Compare the clause against benchmarks.' },
    ],
  },
];

export function LexSelectionPopover({ onAction, onCompareCancel, matterIntelligenceOnly = false }: Props) {
  const [selection, setSelection] = useState<LexSelectionContext | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [actionPending, setActionPending] = useState(false);
  const [compareBase, setCompareBase] = useState<CompareBaseSelection | null>(null);
  const capturedRangeRef = useRef<Range | null>(null);
  const compareBaseRangeRef = useRef<Range | null>(null);
  const compareBaseSelectionRef = useRef<LexSelectionContext | null>(null);
  const rafRef = useRef<number | null>(null);
  /** Mirrors popover visibility for the document listeners, which close over
   *  a stable effect and cannot read the latest state directly. */
  const popoverOpenRef = useRef(false);

  const clearSelectionUi = () => {
    capturedRangeRef.current = null;
    popoverOpenRef.current = false;
    setSelection(null);
    setPosition(null);
    setActionPending(false);
  };

  const clearCompareBase = (notifyParent = false) => {
    compareBaseRangeRef.current = null;
    compareBaseSelectionRef.current = null;
    setCompareBase(null);
    if (notifyParent) {
      onCompareCancel?.();
    }
  };

  useEffect(() => {
    const refreshCapturedRange = () => {
      const range = capturedRangeRef.current;
      if (range) {
        try {
          const rects = selectionRectsFromRange(range);
          const nextPosition = positionFromRects(rects);
          if (!nextPosition || rects.length === 0) return;
          setPosition(nextPosition);
        } catch {
          clearSelectionUi();
        }
      }

      const compareRange = compareBaseRangeRef.current;
      const compareSelection = compareBaseSelectionRef.current;
      if (compareRange && compareSelection) {
        try {
          const rects = selectionRectsFromRange(compareRange);
          if (rects.length === 0) return;
          setCompareBase({ selection: compareSelection, rects });
        } catch {
          clearCompareBase(true);
        }
      }
    };

    const scheduleRefresh = () => {
      if (!capturedRangeRef.current && !compareBaseRangeRef.current) return;
      if (rafRef.current != null) return;
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        refreshCapturedRange();
      });
    };

    const updateSelection = (event?: Event) => {
      const target = event?.target instanceof Element ? event.target : null;
      if (target?.closest('[data-lex-selection-popover="true"]')) return;

      window.setTimeout(() => {
        const activeSelection = window.getSelection();
        if (!activeSelection || activeSelection.rangeCount === 0 || activeSelection.isCollapsed) {
          // A click inside selectable text collapses the selection. mousedown
          // deliberately ignores that region (it may be the start of a new
          // drag-select), so this is where a plain click-away must dismiss —
          // otherwise the popover survived until Escape (David, 2026-08-05).
          if (popoverOpenRef.current) {
            clearSelectionUi();
          }
          return;
        }
        const range = activeSelection.getRangeAt(0);
        const rects = selectionRectsFromRange(range);
        const nextPosition = positionFromRects(rects);
        if (!nextPosition) return;
        const context = selectionFromRange(activeSelection, range, rects);
        if (!context) {
          clearSelectionUi();
          return;
        }

        const baseSelection = compareBaseSelectionRef.current;
        if (baseSelection) {
          const isSameSelection =
            baseSelection.id === context.id ||
            (
              baseSelection.text === context.text &&
              baseSelection.documentId === context.documentId &&
              baseSelection.page === context.page
            );
          if (!isSameSelection) {
            onAction('compare', context);
            clearCompareBase();
            window.getSelection()?.removeAllRanges();
            clearSelectionUi();
            return;
          }
        }

        capturedRangeRef.current = range.cloneRange();
        popoverOpenRef.current = true;
        setSelection(context);
        setPosition(nextPosition);
        setActionPending(false);
      }, 0);
    };

    const clear = (event: Event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('[data-lex-selection-popover="true"]')) return;
      if (target?.closest('[data-lex-selectable="true"]')) {
        return;
      }
      window.getSelection()?.removeAllRanges();
      clearSelectionUi();
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      window.getSelection()?.removeAllRanges();
      clearSelectionUi();
      clearCompareBase(true);
    };

    const handleExternalSelection = (event: Event) => {
      const detail = (event as CustomEvent<MatterIntelligenceSelectionEventDetail>).detail;
      if (!detail?.selection?.text || !detail.position) return;
      capturedRangeRef.current = null;
      popoverOpenRef.current = true;
      setSelection(detail.selection);
      setPosition({
        top: Math.max(8, detail.position.top),
        left: Math.min(window.innerWidth - 420, Math.max(8, detail.position.left)),
      });
      setActionPending(false);
    };

    document.addEventListener('mouseup', updateSelection);
    document.addEventListener('keyup', updateSelection);
    document.addEventListener('mousedown', clear);
    document.addEventListener('scroll', scheduleRefresh, true);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener(MATTER_INTELLIGENCE_SELECTION_EVENT, handleExternalSelection);
    window.addEventListener('resize', scheduleRefresh);
    return () => {
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
      }
      document.removeEventListener('mouseup', updateSelection);
      document.removeEventListener('keyup', updateSelection);
      document.removeEventListener('mousedown', clear);
      document.removeEventListener('scroll', scheduleRefresh, true);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener(MATTER_INTELLIGENCE_SELECTION_EVENT, handleExternalSelection);
      window.removeEventListener('resize', scheduleRefresh);
    };
  }, [onAction, onCompareCancel]);

  const hasPopover = Boolean(selection && position);

  const runAction = (action: SelectionAction) => {
    if (actionPending || !selection) return;
    setActionPending(true);
    if (action === 'compare') {
      if (compareBaseSelectionRef.current) {
        onAction(action, selection);
        clearCompareBase();
        window.getSelection()?.removeAllRanges();
        clearSelectionUi();
        return;
      }
      const baseRange = capturedRangeRef.current;
      const baseRects = baseRange ? selectionRectsFromRange(baseRange) : [];
      compareBaseRangeRef.current = baseRange?.cloneRange() ?? null;
      compareBaseSelectionRef.current = selection;
      setCompareBase({ selection, rects: baseRects });
      onAction(action, selection);
      window.getSelection()?.removeAllRanges();
      clearSelectionUi();
      return;
    }
    onAction(action, selection);
    if (action !== 'add_to_prompt' && action !== 'ask') {
      window.getSelection()?.removeAllRanges();
      clearSelectionUi();
    } else {
      window.setTimeout(() => setActionPending(false), 250);
    }
  };

  if (matterIntelligenceOnly) {
    return hasPopover ? (
      <div data-lex-selection-popover="true" className="fixed z-[1000] rounded-lg border border-blue-200 bg-white p-2 shadow-xl dark:border-blue-900 dark:bg-slate-900" style={{ top: position!.top, left: position!.left }}>
        <button type="button" disabled={actionPending} onClick={() => runAction('matter_intelligence')} className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-50 dark:bg-blue-950 dark:text-blue-200">
          <DocumentPlusIcon className="h-4 w-4" /> Save to Matter Intelligence
        </button>
      </div>
    ) : null;
  }

  return (
    <>
      {compareBase && (
        <style>
          {'[data-lex-selectable="true"]::selection { background: rgba(245, 158, 11, 0.42); color: inherit; }'}
        </style>
      )}
      {compareBase && (
        <div className="pointer-events-none fixed inset-0 z-[999]" aria-hidden="true">
          {compareBase.rects.map((rect, index) => (
            <div
              key={`${compareBase.selection.id}-${index}`}
              className="absolute rounded-sm bg-blue-500/30 ring-1 ring-blue-500/40"
              style={{
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
              }}
            />
          ))}
        </div>
      )}
      {compareBase && !hasPopover && (
        <div
          data-lex-selection-popover="true"
          className="fixed bottom-4 left-1/2 z-[1000] -translate-x-1/2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-950 shadow-lg"
        >
          First comparison passage saved. Select the second passage to compare.
        </div>
      )}
      {hasPopover && (
      <div
        data-lex-selection-popover="true"
        className="fixed z-[1000] max-w-[460px] rounded-lg border border-[var(--lex-border)] bg-[var(--lex-surface-card)] p-2 shadow-xl"
        style={{ top: position!.top, left: position!.left }}
      >
        <div className="mb-1.5 flex items-center justify-between gap-2 px-1 text-[11px] font-medium text-[var(--lex-text-tertiary)]">
          <span className="max-w-[330px] truncate">{selection!.documentTitle || selection!.label || 'Selected text'}</span>
          <button
            type="button"
            onClick={() => {
              window.getSelection()?.removeAllRanges();
              clearSelectionUi();
              clearCompareBase(true);
            }}
            className="rounded px-1 text-[var(--lex-text-tertiary)] hover:bg-[var(--lex-surface-raised)] hover:text-[var(--lex-text-primary)]"
            aria-label="Clear selected text"
          >
            Esc
          </button>
        </div>
        <div className="space-y-2">
          {SELECTION_ACTION_GROUPS.map((group) => {
            const actions = group.actions.filter((definition) => (
              definition.action !== 'matter_intelligence'
              || import.meta.env.VITE_EDGELEX_MATTER_CONTEXT_CAPTURE !== 'false'
            ));
            if (actions.length === 0) return null;
            return (
              <div key={group.key}>
                <div className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--lex-text-tertiary)]">
                  {group.heading}
                </div>
                <div className="flex flex-wrap gap-1">
                  {actions.map((definition) => (
                    <button
                      key={definition.action}
                      type="button"
                      disabled={actionPending}
                      title={definition.hint}
                      onClick={() => runAction(definition.action)}
                      className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium disabled:opacity-50 ${
                        definition.primary
                          ? 'bg-[var(--lex-surface-raised)] text-[color:var(--lex-accent-primary)] hover:bg-[var(--lex-surface-muted)]'
                          : 'text-[var(--lex-text-secondary)] hover:bg-[var(--lex-surface-raised)]'
                      }`}
                    >
                      <span aria-hidden="true">{definition.emoji}</span>
                      {definition.action === 'compare' && compareBase ? 'Compare now' : definition.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}
    </>
  );
}
